<!--
  MetricWidget.svelte — renders one dashboard widget.

  A widget is a pure renderer over the page's batch results: it never queries on
  its own. The dashboard fetches everything in one request and hands each widget
  its slice by key, which is what turns a twenty-widget tab from forty requests
  into one. A failed query carries its own error, so a broken widget shows why
  rather than sitting silently empty.
-->
<script lang="ts">
	import { onDestroy, tick } from 'svelte';
	import ApexCharts from 'apexcharts';
	import { Spinner } from 'flowbite-svelte';
	import { t, t2 } from '../../../i18n';
	import { RoutedLink } from '../../../components';
	import type {
		AnalyticsBatchCohortResult,
		AnalyticsBatchResult,
		AnalyticsCatalog,
		AnalyticsSeries,
		CohortRow,
		DashboardWidget
	} from '../../../AppTypes';
	import { widgetQueryKey, widgetWindow, type WidgetQueryPart } from '../../../analytics/dashboardQueries';
	import {
		buildChartOptions,
		buildGaugeOptions,
		buildSparklineOptions,
		buildWaterfallOptions,
		downloadFile,
		formatValue,
		groupSegments,
		resolveThreshold,
		seriesToCsv,
		type GroupSegment
	} from '../../../analytics/metricCharts';

	interface Props {
		/** The widget to render. */
		widget: DashboardWidget;
		/** The metric catalogue, for field definitions and fill strategies. */
		catalog?: AnalyticsCatalog;
		/** A page-wide range override in days; 0 means use the widget's own. */
		rangeOverride?: number;
		/** The page's batch results, keyed by {@link widgetQueryKey}. */
		results?: Map<string, AnalyticsBatchResult>;
		/** The page's cohort results, keyed the same way. */
		cohortResults?: Map<string, AnalyticsBatchCohortResult>;
		/** Whether the page's batch is still in flight. */
		pageLoading?: boolean;
		/** Asks the page to refetch. A failed query belongs to the batch, not to this widget. */
		onretry?: () => void;
	}

	let {
		widget,
		catalog = undefined,
		rangeOverride = 0,
		results = new Map(),
		cohortResults = new Map(),
		pageLoading = false,
		onretry = undefined
	}: Props = $props();

	/** The element the main chart renders into. */
	let chartElement: HTMLDivElement | undefined = $state();
	/** The element the sparkline renders into. */
	let sparkElement: HTMLDivElement | undefined = $state();
	/** The live main chart, if any. */
	let chart: ApexCharts | undefined;
	/** The live sparkline, if any. */
	let spark: ApexCharts | undefined;

	/** Whether this widget is waiting on data. */
	let loading = $state(true);
	/** The reason this widget could not render, if any. */
	let error: string | null = $state(null);
	/** Whether a chart is currently mounted, which gates the PNG export. */
	let hasChart = $state(false);

	/** The unit the headline value is expressed in. */
	let unit = $state('count');
	/** The formatted headline value. */
	let valueText = $state('');
	/** The colour a tripped threshold assigns, if any. */
	let valueColor: string | null = $state(null);
	/** The period-over-period change, as a percentage. */
	let deltaPercent: number | null = $state(null);
	/** Whether that change was upward. */
	let deltaUp = $state(false);

	/** One stage of a funnel. */
	interface Stage {
		label: string;
		value: number;
		percentOfFirst: number;
		percentOfPrevious: number;
	}

	/** The funnel stages, in order. */
	let stages: Stage[] = $state([]);

	/** The cohorts of a retention matrix. */
	let cohorts: CohortRow[] = $state([]);
	/** The highest month offset the matrix reports. */
	let cohortMaxOffset = $state(0);

	/** One row of a ranked table. A row keeps its segments so each dimension can link. */
	interface Row {
		label: string;
		value: number;
		segments: GroupSegment[];
	}

	/** The table rows, highest first. */
	let rows: Row[] = $state([]);
	/** The largest row value, which scales the bars. */
	let rowMax = $state(0);

	/** The series last rendered, for the CSV export. */
	let lastSeries: AnalyticsSeries[] = $state([]);

	/** Destroys both charts. ApexCharts leaks its SVG and listeners otherwise. */
	function destroyCharts(): void {
		chart?.destroy();
		chart = undefined;
		spark?.destroy();
		spark = undefined;
		hasChart = false;
	}

	/**
	 * Reads one of this widget's batch results.
	 *
	 * @param part - Which piece of the widget's data to read.
	 * @returns The result, or null when the page has not delivered it.
	 */
	function resultFor(part: WidgetQueryPart): AnalyticsBatchResult | null {
		return results.get(widgetQueryKey(widget.id, part)) ?? null;
	}

	/**
	 * The series of one of this widget's results.
	 *
	 * @param part - Which piece of the widget's data to read.
	 * @returns The series, empty when the page has not delivered it.
	 */
	function seriesFor(part: WidgetQueryPart): AnalyticsSeries[] {
		const result = resultFor(part);
		if (result == null) return [];
		/* A failed query reports its reason here; surfacing it keeps a broken widget legible. */
		if (result.Error != null) throw new Error(result.Error);
		return result.Series ?? [];
	}

	/**
	 * Renders a headline value, with its optional delta, sparkline or gauge.
	 *
	 * @remarks
	 * Everything is computed into locals and assigned to state once. Reading a `$state`
	 * back inside the effect that wrote it makes the effect depend on its own write, and
	 * Svelte 5 then re-runs it until it gives up with `effect_update_depth_exceeded`.
	 */
	async function renderValue(): Promise<void> {
		const resultSeries = seriesFor('total');
		const seriesUnit = resultSeries[0]?.Unit ?? 'count';
		const total = resultSeries[0]?.Points[0]?.Value ?? 0;
		const color = resolveThreshold(total, widget.thresholds, widget.thresholdDirection);

		unit = seriesUnit;
		valueText = formatValue(total, seriesUnit);
		valueColor = color;
		lastSeries = resultSeries;

		/* Only a comparing widget pays for the extra previous-period query. */
		if (widget.compare === true) {
			const previous = seriesFor('previous');
			const previousValue = previous[0]?.Points[0]?.Value ?? 0;
			/* A previous period of zero has no percentage change to report. */
			const delta = previousValue !== 0 ? ((total - previousValue) / previousValue) * 100 : null;
			deltaPercent = delta;
			deltaUp = delta != null && delta >= 0;
		} else {
			deltaPercent = null;
		}

		const sparkSeries = widget.viz === 'value' && widget.sparkline === true ? seriesFor('sparkline') : [];
		loading = false;
		await tick();

		/* A gauge draws a dial; a value with a sparkline draws the trend behind the number. */
		if (widget.viz === 'gauge') {
			renderChartFrom(buildGaugeOptions(total, widget.target ?? 0, seriesUnit, color ?? undefined, 240));
		} else if (sparkSeries.length > 0) {
			renderSparkline(
				sparkSeries[0].Points.map((point) => point.Value),
				seriesUnit,
				color
			);
		} else {
			destroyCharts();
		}
	}

	/** Renders a ranked top-N table. */
	function renderTable(): void {
		const resultSeries = seriesFor('main');
		const fields = catalog?.Metrics.find((metric) => metric.Key === widget.metric)?.Fields ?? [];
		const aggregated: Record<string, Row> = {};
		/* Collapse rows that share a label, which several dimensions can produce. */
		for (const point of resultSeries[0]?.Points ?? []) {
			const segments = groupSegments(point, fields);
			const label = segments.map((segment) => segment.label).join(' / ') || $t('Total');
			const existing = aggregated[label];
			if (existing != null) existing.value += point.Value;
			else aggregated[label] = { label, value: point.Value, segments };
		}

		/* Ranked in a local, so the max is read off that rather than off the state just written. */
		const ranked = Object.values(aggregated).sort((a, b) => b.value - a.value);
		unit = resultSeries[0]?.Unit ?? 'count';
		lastSeries = resultSeries;
		rows = ranked;
		rowMax = ranked.reduce((max, row) => Math.max(max, row.value), 0);
		loading = false;
		destroyCharts();
	}

	/** Renders an ordered conversion funnel. */
	function renderFunnel(): void {
		/* The engine returns one series per metric in request order — the stage order. */
		const resultSeries = seriesFor('main');
		lastSeries = resultSeries;
		unit = resultSeries[0]?.Unit ?? 'count';
		const totals = resultSeries.map((entry) => ({ label: entry.Label, value: entry.Points[0]?.Value ?? 0 }));
		const first = totals[0]?.value ?? 0;
		stages = totals.map((stage, index) => ({
			label: stage.label,
			value: stage.value,
			percentOfFirst: first > 0 ? (stage.value / first) * 100 : 0,
			percentOfPrevious: index === 0 ? 100 : totals[index - 1].value > 0 ? (stage.value / totals[index - 1].value) * 100 : 0
		}));
		loading = false;
		destroyCharts();
	}

	/** Renders a cohort-retention heatmap. */
	function renderCohort(): void {
		const result = cohortResults.get(widgetQueryKey(widget.id, 'cohort')) ?? null;
		/* The cohort query carries its own error, isolated the same way a metric query is. */
		if (result?.Error != null) throw new Error(result.Error);
		cohorts = result?.Cohort?.cohorts ?? [];
		cohortMaxOffset = result?.Cohort?.maxOffset ?? 0;
		loading = false;
		destroyCharts();
	}

	/**
	 * A blue heatmap colour for a retention percentage.
	 *
	 * @param percent - The retention, 0 to 100.
	 * @returns An rgba colour; near-zero stays very faint rather than invisible.
	 */
	function cohortColor(percent: number): string {
		const intensity = Math.max(0, Math.min(1, percent / 100));
		return `rgba(59, 130, 246, ${0.08 + intensity * 0.8})`;
	}

	/** Renders a waterfall of several metrics' totals. */
	async function renderWaterfall(): Promise<void> {
		const resultSeries = seriesFor('main');
		const seriesUnit = resultSeries[0]?.Unit ?? 'count';
		const totals = resultSeries.map((entry) => ({ label: entry.Label, value: entry.Points[0]?.Value ?? 0 }));
		lastSeries = resultSeries;
		unit = seriesUnit;
		loading = false;
		await tick();
		renderChartFrom(
			buildWaterfallOptions(totals, seriesUnit, 240, {
				toolbar: { show: false },
				zoom: { enabled: false },
				animations: { enabled: false }
			})
		);
	}

	/** Renders a time-series or grouped chart. */
	async function renderChartWidget(): Promise<void> {
		const { fromDate, toDate } = widgetWindow(widget, rangeOverride);
		const resultSeries = seriesFor('main');
		unit = resultSeries[0]?.Unit ?? 'count';
		lastSeries = resultSeries;
		loading = false;
		/* Let Svelte lay the container out before ApexCharts measures it. */
		await tick();

		renderChartFrom(
			buildChartOptions(resultSeries, {
				granularity: widget.granularity ?? 'day',
				fromDate,
				toDate,
				metricFill: (key) => catalog?.Metrics.find((metric) => metric.Key === key)?.Fill ?? 'zero',
				valueLabel: $t('Value'),
				height: 240,
				style: 'area',
				chartType: widget.chartType ?? 'area',
				annotations: widget.annotations,
				goal: widget.goal
			})
		);
	}

	/**
	 * Mounts a chart from prepared options, replacing whatever was there.
	 *
	 * @param options - The ApexCharts options, or null when there is nothing to draw.
	 */
	function renderChartFrom(options: Record<string, unknown> | null): void {
		spark?.destroy();
		spark = undefined;
		chart?.destroy();
		chart = undefined;
		hasChart = false;
		/* Nothing to draw, or nowhere to draw it. */
		if (options == null || chartElement == null) return;
		chart = new ApexCharts(chartElement, options);
		chart.render();
		hasChart = true;
	}

	/**
	 * Mounts the trend sparkline behind a headline value.
	 *
	 * @param values     - One value per bucket.
	 * @param seriesUnit - The unit, for the tooltip.
	 * @param color      - The colour a tripped threshold assigns, if any.
	 */
	function renderSparkline(values: number[], seriesUnit: string, color: string | null): void {
		spark?.destroy();
		spark = undefined;
		/* Nowhere to draw, or nothing to draw there. */
		if (sparkElement == null || values.length === 0) return;
		spark = new ApexCharts(sparkElement, buildSparklineOptions(values, seriesUnit, color ?? undefined));
		spark.render();
	}

	/** Renders this widget from the page's batch results. Does no fetching of its own. */
	async function render(): Promise<void> {
		error = null;
		/* Any failure here — a failed query, a malformed result — becomes this widget's error. */
		try {
			if (widget.viz === 'value' || widget.viz === 'gauge') await renderValue();
			else if (widget.viz === 'table') renderTable();
			else if (widget.viz === 'funnel') renderFunnel();
			else if (widget.viz === 'waterfall') await renderWaterfall();
			else if (widget.viz === 'cohort') renderCohort();
			else await renderChartWidget();
		} catch (caught) {
			error = caught instanceof Error ? caught.message : String(caught);
			loading = false;
			destroyCharts();
		}
	}

	/** Downloads the rendered series as CSV. */
	function fnExportCsv(): void {
		if (lastSeries.length === 0) return;
		downloadFile(`${widget.title || widget.metric}.csv`, seriesToCsv(lastSeries), 'text/csv');
	}

	/** Downloads the rendered chart as a PNG. */
	async function fnExportPng(): Promise<void> {
		if (chart == null) return;
		const { imgURI } = await (chart as unknown as { dataURI: () => Promise<{ imgURI: string }> }).dataURI();
		const anchor = document.createElement('a');
		anchor.href = imgURI;
		anchor.download = `${widget.title || widget.metric}.png`;
		anchor.click();
	}

	onDestroy(destroyCharts);

	/**
	 * Re-render when the widget's own configuration changes, or when the page delivers a
	 * new batch. `results` is replaced wholesale by the page on every load, so its identity
	 * is what signals fresh data.
	 */
	let renderKey = $derived(
		JSON.stringify([
			widget.viz,
			widget.chartType,
			widget.metric,
			widget.metrics ?? [],
			widget.granularity,
			widget.thresholds ?? [],
			widget.thresholdDirection,
			widget.annotations ?? [],
			widget.goal,
			widget.target,
			widget.compare,
			widget.sparkline
		])
	);

	$effect(() => {
		/* While the page's batch is in flight, show the loading state rather than stale data. */
		if (pageLoading) {
			loading = true;
			return;
		}
		/* Touch both so the effect re-runs when either changes. */
		void renderKey;
		void results;
		void cohortResults;
		render();
	});
</script>

<div class="group/widget relative h-full">
	<div class="mb-1 flex items-center">
		<div class="truncate text-sm font-semibold">{$t(widget.title)}</div>
		<span class="grow"></span>
		{#if !loading && error == null && lastSeries.length > 0}
			<div class="flex items-center gap-2 opacity-0 transition-opacity group-hover/widget:opacity-100">
				<button
					type="button"
					title={$t('Export CSV')}
					aria-label={$t('Export CSV')}
					class="text-gray-400 hover:text-primary-600"
					onclick={fnExportCsv}
				>
					<i class="fa-solid fa-file-csv text-xs"></i>
				</button>
				{#if hasChart}
					<button
						type="button"
						title={$t('Export PNG')}
						aria-label={$t('Export PNG')}
						class="text-gray-400 hover:text-primary-600"
						onclick={fnExportPng}
					>
						<i class="fa-solid fa-image text-xs"></i>
					</button>
				{/if}
			</div>
		{/if}
	</div>

	{#if error != null}
		<div class="flex flex-col items-center justify-center gap-2 text-center" style="min-height:120px">
			<span class="break-all text-xs text-red-600 dark:text-red-400">{error}</span>
			<button
				type="button"
				class="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"
				onclick={() => onretry?.()}
			>
				<i class="fa-solid fa-rotate-right text-xs"></i>{$t('Retry')}
			</button>
		</div>
	{:else if widget.viz === 'value'}
		<div class="flex flex-col items-center justify-center" style="min-height:120px">
			{#if loading}
				<Spinner size="6" />
			{:else}
				<span
					class="text-4xl font-bold {valueColor == null ? 'text-gray-800 dark:text-gray-100' : ''}"
					style={valueColor != null ? `color:${valueColor}` : ''}>{valueText}</span
				>
				{#if deltaPercent != null}
					<span
						class="mt-1 inline-flex items-center gap-1 text-xs font-medium {deltaUp
							? 'text-emerald-600'
							: 'text-rose-600'}"
					>
						<i class="fa-solid {deltaUp ? 'fa-arrow-up' : 'fa-arrow-down'} text-[10px]"></i>
						{Math.abs(deltaPercent).toFixed(1)}%
						<span class="ml-1 text-gray-400">{$t('vs. previous')}</span>
					</span>
				{/if}
				{#if widget.sparkline}
					<div class="-mb-2 mt-1 w-full"><div bind:this={sparkElement}></div></div>
				{/if}
			{/if}
		</div>
	{:else if widget.viz === 'gauge'}
		<div class="relative flex items-center justify-center" style="min-height:240px">
			<div bind:this={chartElement} class="w-full"></div>
			{#if loading}
				<div class="absolute inset-0 flex items-center justify-center"><Spinner size="6" /></div>
			{/if}
		</div>
	{:else if widget.viz === 'funnel'}
		<div style="min-height:120px">
			{#if loading}
				<div class="flex items-center justify-center py-8"><Spinner size="6" /></div>
			{:else if stages.length === 0}
				<p class="py-8 text-center text-sm text-gray-400">{$t('No data.')}</p>
			{:else}
				<div class="mt-1 space-y-2">
					{#each stages as stage, index (stage.label)}
						<div>
							<div class="mb-0.5 flex items-center justify-between text-xs">
								<span class="mr-2 truncate text-gray-600 dark:text-gray-300">{stage.label}</span>
								<span class="whitespace-nowrap font-semibold tabular-nums"
									>{formatValue(stage.value, unit)}</span
								>
							</div>
							<div class="relative h-5 overflow-hidden rounded bg-depth">
								<div
									class="absolute inset-y-0 left-0 rounded bg-primary-500/70"
									style="width:{stage.percentOfFirst}%"
								></div>
								<span class="absolute right-1 top-0 text-[10px] leading-5 text-gray-500 dark:text-gray-400"
									>{stage.percentOfFirst.toFixed(0)}%</span
								>
							</div>
							{#if index > 0}
								<div class="mt-0.5 text-[10px] text-gray-400">
									{t2('{{percent}}% from previous', { percent: stage.percentOfPrevious.toFixed(0) })}
								</div>
							{/if}
						</div>
					{/each}
				</div>
			{/if}
		</div>
	{:else if widget.viz === 'cohort'}
		<div style="min-height:120px">
			{#if loading}
				<div class="flex items-center justify-center py-8"><Spinner size="6" /></div>
			{:else if cohorts.length === 0}
				<p class="py-8 text-center text-sm text-gray-400">{$t('No data.')}</p>
			{:else}
				<!--
					No horizontal scroll: the matrix shrinks to the widget instead. A sideways
					scrollbar inside an already-scrolling page hides columns with nothing saying
					so, and a cohort grid of thirteen two-character cells fits without one — a
					narrow widget makes it dense rather than clipped.
				-->
				<div class="-mx-2">
					<table class="w-full table-fixed border-separate text-[10px]" style="border-spacing:2px">
						<thead>
							<tr class="text-gray-400">
								<th class="px-1 text-left font-medium">{$t('Cohort')}</th>
								<th class="px-1 text-right font-medium">{$t('Users')}</th>
								{#each Array(cohortMaxOffset + 1) as _unused, offset (offset)}
									<th class="px-1 text-center font-medium">{offset}</th>
								{/each}
							</tr>
						</thead>
						<tbody>
							{#each cohorts as row (row.cohort)}
								<tr>
									<td class="whitespace-nowrap pr-2 text-gray-600 dark:text-gray-300">{row.cohort.slice(0, 7)}</td>
									<td class="pr-2 text-right tabular-nums text-gray-500">{row.size.toLocaleString()}</td>
									{#each row.retention as percent, offset (offset)}
										<td
											class="rounded text-center tabular-nums text-gray-700 dark:text-gray-100"
											style="background:{cohortColor(percent)}">{percent.toFixed(0)}%</td
										>
									{/each}
								</tr>
							{/each}
						</tbody>
					</table>
				</div>
			{/if}
		</div>
	{:else if widget.viz === 'table'}
		<div style="min-height:120px">
			{#if loading}
				<div class="flex items-center justify-center py-8"><Spinner size="6" /></div>
			{:else if rows.length === 0}
				<p class="py-8 text-center text-sm text-gray-400">{$t('No data.')}</p>
			{:else}
				<div class="mt-1 space-y-1.5">
					{#each rows as row (row.label)}
						<div class="relative">
							<div
								class="absolute inset-y-0 left-0 rounded bg-primary-100 dark:bg-primary-900/40"
								style="width:{rowMax > 0 ? (row.value / rowMax) * 100 : 0}%"
							></div>
							<div class="relative flex items-center justify-between px-2 py-1 text-sm">
								<span class="mr-2 truncate">
									{#each row.segments as segment, index (segment.key)}
										{#if index > 0}<span class="text-gray-400"> / </span>{/if}
										{#if segment.href != null}
											<RoutedLink to={segment.href} class="text-primary-600 hover:underline"
												>{segment.label}</RoutedLink
											>
										{:else}
											{segment.label}
										{/if}
									{:else}
										{row.label}
									{/each}
								</span>
								<span class="whitespace-nowrap font-semibold tabular-nums">{formatValue(row.value, unit)}</span>
							</div>
						</div>
					{/each}
				</div>
			{/if}
		</div>
	{:else}
		<!-- Full-bleed horizontally so the plot grips the card edges; the heading keeps its padding. -->
		<div class="relative -mx-4 sm:-mx-6">
			<div bind:this={chartElement} style="min-height:240px"></div>
			{#if loading}
				<div class="absolute inset-0 flex items-center justify-center">
					<Spinner size="6" />
				</div>
			{/if}
		</div>
	{/if}
</div>
